import { createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { user as userTable } from "@boveda/powersync";
import {
  getIncomingSharesQuery,
  openIncomingShareRow,
  queryKeys,
  type VaultItemPlain,
} from "@boveda/shared";
import { createPowerSyncQuery } from "@boveda/shared/solid";
import { Check, Eye, EyeOff, Inbox, LockOpen, Save } from "@fleet/icons";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { useI18n } from "@/providers/i18n-provider";
import { useVault } from "../vault-context";

interface ShareRow {
  id: string;
  from_user_id: string | null;
  to_user_id: string | null;
  sealed: string | null;
  created_at: string | null;
}

interface UserRow {
  id: string;
  name: string | null;
  email: string | null;
}

/**
 * Secrets shared WITH ME: each row is an ECIES envelope sealed to my public key.
 * public key received via sync. Opened LOCALLY with my private key (requires
 * vault unlocked); "save" re-encrypts it into my personal vault.
 */
export function SharesPage() {
  const { t } = useI18n();
  const vault = useVault();
  const { db } = usePowerSyncContext();
  // Open envelopes in memory (id → plain) + ids already saved in my vault.
  const [openedShares, setOpenedShares] = createSignal<Record<string, VaultItemPlain>>({});
  const [savedIds, setSavedIds] = createSignal<Record<string, boolean>>({});
  const [error, setError] = createSignal("");

  const sharesQuery = createPowerSyncQuery<ShareRow>({
    queryKey: () => queryKeys.vault.shares(vault.userId()),
    queryFn: () => {
      const d = vault.drizzle();
      return d ? getIncomingSharesQuery(d, vault.userId()) : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()) && Boolean(vault.userId()),
  });

  const senders = createPowerSyncQuery<UserRow>({
    queryKey: () => queryKeys.vault.memberNames(`senders-${vault.userId()}`),
    queryFn: () => {
      const d = vault.drizzle();
      return d
        ? d
            .select({ id: userTable.id, name: userTable.name, email: userTable.email })
            .from(userTable)
        : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()),
  });

  const senderName = createMemo(() => {
    const users = new Map((senders.data ?? []).map((u) => [u.id, u]));
    return (userId: string | null): string => {
      if (!userId) {
        return t(msg`Unknown sender`);
      }
      const u = users.get(userId);
      return u?.name || u?.email || userId;
    };
  });

  async function open(row: ShareRow): Promise<void> {
    setError("");
    try {
      const acc = vault.account();
      if (!acc) {
        throw new Error("vault is locked");
      }
      const plain = await openIncomingShareRow(acc.privateKey, row);
      setOpenedShares((prev) => ({ ...prev, [row.id]: plain }));
    } catch {
      setError(t(msg`Could not open this envelope with your key.`));
    }
  }

  async function saveToVault(rowId: string): Promise<void> {
    const plain = openedShares()[rowId];
    if (!plain) {
      return;
    }
    await vault.saveItem(plain);
    setSavedIds((prev) => ({ ...prev, [rowId]: true }));
  }

  return (
    <div class="mx-auto w-full max-w-2xl px-4 py-6" onPointerDown={() => vault.keepAlive()}>
      <h1 class="mb-1 text-xl font-semibold">{t(msg`Shared with me`)}</h1>
      <p class="mb-4 text-sm text-muted-foreground">
        {t(
          msg`Each entry is an envelope sealed to your public key. It is opened locally with your private key — the server only relayed it.`,
        )}
      </p>

      <Show
        when={(sharesQuery.data ?? []).length > 0}
        fallback={
          <EmptyState
            icon={Inbox}
            title={t(msg`Nothing shared with you yet`)}
            description={t(msg`When someone shares a secret with you, it will appear here.`)}
          />
        }
      >
        <ul class="flex flex-col gap-2" data-testid="share-list">
          <For each={sharesQuery.data ?? []}>
            {(row) => {
              const plain = () => openedShares()[row.id];
              return (
                <li
                  class="flex flex-col gap-2 rounded-lg border border-border/50 bg-card/40 px-3 py-2.5"
                  data-testid="share-row"
                >
                  <div class="flex items-center gap-3">
                    <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Inbox class="h-4 w-4" />
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-sm font-medium">
                        {plain()?.name ?? t(msg`Sealed secret`)}
                      </span>
                      <span class="block truncate text-xs text-muted-foreground">
                        {t(msg`From`)} {senderName()(row.from_user_id)}
                      </span>
                    </span>
                    <Show
                      when={plain()}
                      fallback={
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void open(row)}
                          data-testid="share-open"
                        >
                          <LockOpen class="h-4 w-4" /> {t(msg`Open`)}
                        </Button>
                      }
                    >
                      <Show
                        when={!savedIds()[row.id]}
                        fallback={
                          <span
                            class="flex items-center gap-1 text-xs font-medium text-success"
                            data-testid="share-saved"
                          >
                            <Check class="h-4 w-4" /> {t(msg`Saved`)}
                          </span>
                        }
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void saveToVault(row.id)}
                          data-testid="share-save"
                        >
                          <Save class="h-4 w-4" /> {t(msg`Save to my vault`)}
                        </Button>
                      </Show>
                    </Show>
                  </div>

                  <Show when={plain()}>{(opened) => <OpenedSharePreview plain={opened()} />}</Show>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>

      <Show when={error()}>
        <p class="mt-3 text-sm text-destructive" role="alert">
          {error()}
        </p>
      </Show>
    </div>
  );
}

function OpenedSharePreview(props: { plain: VaultItemPlain }) {
  const { t } = useI18n();
  const [revealed, setRevealed] = createSignal(false);
  return (
    <div class="flex flex-col gap-1.5 border-t border-border/40 pt-2" data-testid="share-plain">
      <Show when={props.plain.fields.username}>
        <div class="flex items-center gap-2 text-sm">
          <span class="text-xs font-medium text-muted-foreground">{t(msg`Username`)}</span>
          <span data-testid="share-plain-username">{props.plain.fields.username}</span>
        </div>
      </Show>
      <Show when={props.plain.fields.password}>
        <div class="flex items-center gap-2 text-sm">
          <span class="text-xs font-medium text-muted-foreground">{t(msg`Password`)}</span>
          <code class="font-mono" data-testid="share-plain-password">
            {revealed() ? props.plain.fields.password : "••••••••••••"}
          </code>
          <Button
            variant="ghost"
            size="icon"
            aria-label={revealed() ? t(msg`Hide`) : t(msg`Reveal`)}
            onClick={() => setRevealed((v) => !v)}
            data-testid="share-plain-reveal"
          >
            <Show when={revealed()} fallback={<Eye class="h-4 w-4" />}>
              <EyeOff class="h-4 w-4" />
            </Show>
          </Button>
        </div>
      </Show>
      <Show when={props.plain.notes}>
        <p class="whitespace-pre-wrap text-sm">{props.plain.notes}</p>
      </Show>
    </div>
  );
}
